import { Icon } from '../../design-system/icons';
import { AuthForm, type AuthMode } from './AuthForm';

/**
 * Envase de **pantalla completa** del formulario de auth en mobile
 * (ADR-2026-07-30-registro-pantalla-completa). Fondo blanco a todo el alto, back
 * opcional arriba a la izquierda y el form centrado.
 *
 * Lo usan los dos caminos de mobile, para no duplicar el shell:
 * - `pages/Registro.tsx` — con back al carrusel.
 * - `pages/Auth.tsx` cuando el modo es login o reset — con back **sólo** en reset
 *   ("restablecer contraseña" vuelve al login, ADR-2026-08-03-back-en-restablecer-contrasena).
 *   En login no: a `/auth` se entra directo por URL, no hay pantalla anterior adonde volver.
 *
 * El alto sale de `pwa-screen-fixed` (medido por JS en `--app-height`): las unidades
 * de viewport CSS fallan en el navegador de iOS y en la PWA instalada (ADR-652).
 */
export interface AuthPantallaCompletaProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  titleId: string;
  /** Si se pasa, se dibuja el back arriba a la izquierda. */
  onBack?: () => void;
}

export function AuthPantallaCompleta({
  mode,
  onModeChange,
  titleId,
  onBack,
}: AuthPantallaCompletaProps) {
  return (
    <div className="pwa-screen-fixed flex h-[100svh] w-full flex-col overflow-hidden bg-white font-sans">
      {/* Header: sólo el back (mismo ícono y peso que el del cuestionario). Sin
          `onBack` igual se reserva el alto, así el form no salta entre pantallas. */}
      <div className="shrink-0 px-5 pb-2 pt-[calc(env(safe-area-inset-top)+20px)]">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Volver"
            className="-m-1 rounded-full p-1 text-[#18181B] transition-colors hover:bg-[#F4F4F5]"
          >
            <Icon name="outline/move-left" size={24} />
          </button>
        ) : (
          <div className="h-6" aria-hidden />
        )}
      </div>

      {/* Contenido: centrado vertical cuando entra (login/reset) y scrolleable
          cuando no (registro con el teclado abierto en pantallas chicas). */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col justify-center py-6">
          <AuthForm
            mode={mode}
            onModeChange={onModeChange}
            titleId={titleId}
            variant="page"
          />
        </div>
      </div>
    </div>
  );
}

export default AuthPantallaCompleta;
